import { BaseAnimation } from './BaseAnimation';
import { Point } from '../objects/Point';
import { Line } from '../objects/Line';
import { Segment } from '../objects/Segment';
import { Ray } from '../objects/Ray';
import { Circle } from '../objects/Circle';
import { Arc } from '../objects/Arc';
import { Compass } from '../objects/Compass';
import { Ruler } from '../objects/Ruler';
import { AnimationEngine } from '../core/AnimationEngine';

export class DrawPoint extends BaseAnimation {
  constructor(target: Point, easing = AnimationEngine.Easings.easeOutBack) {
    super(target, easing);
  }

  protected update(progress: number): void {
    const point = this.target as Point;
    if (typeof point.setVisible === 'function') {
      point.setVisible(progress > 0);
    }
    if (typeof point.setScale === 'function') {
      point.setScale(progress);
    }
    if (typeof point.setOpacity === 'function') {
      point.setOpacity(Math.min(1, progress * 2));
    }
  }
}

export class DrawLine extends BaseAnimation {
  private ruler?: Ruler;

  constructor(target: Line | Segment | Ray, ruler?: Ruler, easing = AnimationEngine.Easings.easeInOutQuad) {
    super(target, easing);
    this.ruler = ruler;
  }

  protected update(progress: number): void {
    const lineObj = this.target as Line | Segment | Ray;
    lineObj.setVisible(progress > 0);
    lineObj.setDrawProgress(progress);

    if (this.ruler && (lineObj instanceof Segment || lineObj instanceof Line)) {
      this.ruler.setVisible(progress > 0 && progress < 1);
      if (progress > 0 && progress < 1) {
        this.ruler.setPoints(lineObj.p1, lineObj.p2);
      }
    }
  }
}

export class DrawCircle extends BaseAnimation {
  private compass?: Compass;

  constructor(target: Circle, compass?: Compass, easing = AnimationEngine.Easings.easeInOutCubic) {
    super(target, easing);
    this.compass = compass;
  }

  protected update(progress: number): void {
    const circle = this.target as Circle;

    if (progress <= 0) {
      circle.setVisible(false);
      circle.setDrawProgress(0);
      return;
    }

    const movePhaseEnd = 0.2;

    if (progress < movePhaseEnd) {
      circle.setVisible(false);
      circle.setDrawProgress(0);
      if (this.compass) {
        this.compass.setVisible(true);
        this.compass.setPosition(circle.center, circle.radius, 0);
      }
    } else {
      circle.setVisible(true);
      const drawT = (progress - movePhaseEnd) / (1 - movePhaseEnd);
      circle.setDrawProgress(drawT);

      if (this.compass) {
        this.compass.setVisible(drawT < 1);
        const currentAngle = drawT * Math.PI * 2;
        this.compass.setPosition(circle.center, circle.radius, currentAngle);
      }
    }
  }
}

export class DrawArc extends BaseAnimation {
  private compass?: Compass;

  constructor(target: Arc, compass?: Compass, easing = AnimationEngine.Easings.easeInOutCubic) {
    super(target, easing);
    this.compass = compass;
  }

  protected update(progress: number): void {
    const arc = this.target as Arc;

    if (progress <= 0) {
      arc.setVisible(false);
      arc.setDrawProgress(0);
      return;
    }

    const movePhaseEnd = 0.15;

    if (progress < movePhaseEnd) {
      // Phase 1: Move compass needle to center & position pencil tip at startAngle (0 arc drawn yet)
      arc.setVisible(false);
      arc.setDrawProgress(0);
      if (this.compass) {
        this.compass.setVisible(true);
        this.compass.setPosition(arc.center, arc.radius, arc.startAngleRad);
      }
    } else {
      // Phase 2: Sweep arc path live right under advancing pencil tip
      arc.setVisible(true);
      const drawT = (progress - movePhaseEnd) / (1 - movePhaseEnd);
      arc.setDrawProgress(drawT);

      if (this.compass) {
        this.compass.setVisible(drawT < 1);
        const currentAngle = arc.startAngleRad + (arc.endAngleRad - arc.startAngleRad) * drawT;
        this.compass.setPosition(arc.center, arc.radius, currentAngle);
      }
    }
  }
}
